// 假设 `this.info` 和 `this.queryParams.date`, `this.endDate` 已经从您的 Vue 组件中获取
// 并且已经通过 `dayjs` 计算好了。

import ExcelJS from 'exceljs'

/**
 * 生成宝镁事业部生产周报表的Excel
 * @param {Object} info - 报表数据对象
 * @param {string} startDateStr - 开始日期字符串 (YYYY-MM-DD)
 * @param {string} endDateStr - 结束日期字符串 (YYYY-MM-DD)
 */
export async function generateExcel(info, startDateStr, endDateStr) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('宝镁事业部生产周报表');

  // 设置列宽，与CSS中的800px宽度大致对应
  // 假设每个单位宽度约等于10像素，则800px / 10px = 80单位
  // 为了美观，我们可以调整一下比例，比如每个单位代表8px，那么总宽度约为 8*100 = 800px
  const colWidth = 15;
  worksheet.columns = Array(6 + (info.portOptions?.length || 0)).fill({ width: colWidth });

  // 当前写入的行号
  let currentRow = 1; 

  // --- 1. 主标题 ---
  const mainTitle = `宝镁事业部生产周报表（${formatDateForTitle(startDateStr)} ~ ${formatDateForTitle(endDateStr)}）`;
  worksheet.mergeCells(currentRow, 1, currentRow, 7);
  const mainTitleCell = worksheet.getCell(currentRow, 1);
  mainTitleCell.value = mainTitle;
  mainTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  mainTitleCell.font = { bold: true };
  mainTitleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFB5C6EA' }
  };
  currentRow++;

  // --- 2. 过驳吨位及数据量统计 ---
  worksheet.mergeCells(currentRow, 1, currentRow, 7); 
  const section1TitleCell = worksheet.getCell(currentRow, 1);
  section1TitleCell.value = '过驳吨位及数据量统计';
  section1TitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  section1TitleCell.font = { bold: true };
  section1TitleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFEEEEEE' }
  };
  currentRow++;

  // 第一行数据: 童埠数据
  worksheet.addRow([
    '装货港',
    '童埠装船吨位', info.gbStartPortLoadTonTotal || '-',
    '童埠装载小船数量', info.gbStartPortSmallShipNumTotal || '-',
    '童埠装载大船数量', info.gbStartPortBigShipNumTotal || '-'
  ]);
  currentRow++;

  // 过驳港部分
  const firstPort = info.portOptions?.[0];
  if (firstPort && info.gbEndPort) {
    const portData = info.gbEndPort.find(p => p.portId === firstPort.value);
    // 竖向合并"过驳港"
    worksheet.mergeCells(currentRow, 1, currentRow + info.portOptions.length - 1, 1);
    worksheet.getCell(currentRow, 1).value = '过驳港';
    worksheet.getCell(currentRow, 1).alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([
      `${firstPort.label || '-'}过驳吨位`, portData?.ton || '-',
      `${firstPort.label || '-'}过驳小船数量`, portData?.smallShipNum || '-',
      `${firstPort.label || '-'}过驳大船数量`, portData?.bigShipNum || '-'
    ]);
    currentRow++;
  }

  // 循环添加其他港口的数据行
  if (info.portOptions?.length > 1) {
    for (let i = 1; i < info.portOptions.length; i++) {
      const port = info.portOptions[i];
      const portData = info.gbEndPort.find(p => p.portId === port.value);
      worksheet.addRow([
        `${port.label || '-'}过驳吨位`, portData?.ton || '-',
        `${port.label || '-'}过驳小船数量`, portData?.smallShipNum || '-',
        `${port.label || '-'}过驳大船数量`, portData?.bigShipNum || '-'
      ]);
      currentRow++;
    }
  }

  // 汇总行
  worksheet.addRow([
    '过驳吨位汇总', info.gbEndPortTonTotal || '-',
    '过驳小船数量汇总', info.gbEndPortSmallShipNumTotal || '-',
    '过驳大船数量汇总', info.gbEndPortBigShipNumTotal || '-'
  ]);
  currentRow++;

  // --- 3. 过驳作业效率统计 ---
  worksheet.mergeCells(currentRow, 1, currentRow, 7);
  const section2TitleCell = worksheet.getCell(currentRow, 1);
  section2TitleCell.value = '过驳作业效率统计';
  section2TitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  section2TitleCell.font = { bold: true };
  section2TitleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFEEEEEE' }
  };
  currentRow++;

  // 循环添加各港口效率数据
  if (info.portOptions?.length && info.gbWorkJob) {
    for (const port of info.portOptions) {
      const jobData = info.gbWorkJob.find(j => j.portId === port.value);
      worksheet.addRow([
        `${port.label || '-'}过驳总时长`, jobData?.workTime || '-', '', 
        `${port.label || '-'}每小时作业效率`, jobData?.efficiency || '-', '', '' 
      ]);
    }
    currentRow += info.portOptions.length;
  } else {
    worksheet.addRow(['-', '-', '-', '-', '-', '-', '-']); 
    currentRow++;
  }

  // 效率汇总行
  worksheet.mergeCells(currentRow, 1, currentRow, 2);
  worksheet.getCell(currentRow, 1).value = '过驳总时长汇总';
  worksheet.mergeCells(currentRow, 4, currentRow, 5);
  worksheet.getCell(currentRow, 4).value = '每小时作业效率汇总';
  worksheet.getCell(currentRow, 3).value = info.gbWorkJobTimeTotal || '-';
  worksheet.getCell(currentRow, 6).value = info.gbWorkJobEfficiencyTotal || '-';
  currentRow++;

  // --- 4. 周问题概括 ---
  worksheet.mergeCells(currentRow, 1, currentRow, 7); 
  const section3TitleCell = worksheet.getCell(currentRow, 1);
  section3TitleCell.value = '周问题概括';
  section3TitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  section3TitleCell.font = { bold: true };
  section3TitleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFEEEEEE' }
  };
  currentRow++;

  worksheet.mergeCells(currentRow, 1, currentRow, 7); 
  const problemSummaryCell = worksheet.getCell(currentRow, 1);
  problemSummaryCell.value = info.problemSummary || '';
  problemSummaryCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
  currentRow++;

  // --- 5. 航次统计表 ---
  // 计算航次表需要的总列数
  const voyageTableColSpan = 6 + (info.portOptions?.length || 0);
  worksheet.mergeCells(currentRow, 1, currentRow, voyageTableColSpan); 
  const voyageTitleCell = worksheet.getCell(currentRow, 1);
  voyageTitleCell.value = `航次统计表（${formatDateForTitle(startDateStr)} ~ ${formatDateForTitle(endDateStr)}）`;
  voyageTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  voyageTitleCell.font = { bold: true };
  voyageTitleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFB5C6EA' }
  };
  currentRow++;

  // 循环处理每一类（已签、未签等）
  if (info.voyageTotal?.length) {
    for (let cIndex = 0; cIndex < info.voyageTotal.length; cIndex++) {
      const category = info.voyageTotal[cIndex];
      const detailList = category.detailList || [];

      // 添加表头行
      if (cIndex === 0) {
        // 如果是第一个分类，绘制完整的表头
        const headerRowData = [
          category.label,
          '序号', '船名', '装载吨位', '月航次总数', '周航次总数'
        ];
        // 添加动态的港口列标题
        info.portOptions?.forEach(port => {
          headerRowData.push(`月去${port.label || ''}总数`);
        });
        worksheet.addRow(headerRowData);
        
        // 设置表头样式
        const headerRow = worksheet.lastRow;
        headerRow.eachCell(cell => {
          cell.font = { bold: true };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFEEEEEE' }
          };
        });
        // 竖向合并分类单元格
        worksheet.mergeCells(currentRow, 1, currentRow + detailList.length, 1);
        worksheet.getCell(currentRow, 1).value = category.label;
        worksheet.getCell(currentRow, 1).alignment = { vertical: 'middle', horizontal: 'center' };
        currentRow++;
      } else {
        // 如果不是第一个分类，只绘制数据行，不重复表头
        if (detailList.length > 0) {
          // 竖向合并分类单元格
          worksheet.mergeCells(currentRow, 1, currentRow + detailList.length - 1, 1);
          worksheet.getCell(currentRow, 1).value = category.label;
          worksheet.getCell(currentRow, 1).alignment = { vertical: 'middle', horizontal: 'center' };
        }
      }

      // 添加明细数据行
      if (detailList.length > 0) {
        for (let i = 0; i < detailList.length; i++) {
          const item = detailList[i];
          // 分类列已在合并单元格中
          const rowData = [
            '', 
            item.seq || '-',
            item.shipName || '-',
            item.realTon || '-',
            item.monthVoyageNum || '-',
            item.weekVoyageNum || '-'
          ];
          // 添加动态的港口列数据
          info.portOptions?.forEach(port => {
            const portData = item.monthPortDetail?.find(p => p.portId === port.value);
            rowData.push(portData?.monthPortNum || '-');
          });
          worksheet.addRow(rowData);
        }
        currentRow += detailList.length;
      } else {
        // 如果明细为空，添加一个空行
        const emptyRowData = [''];
        for (let i = 0; i < 5 + (info.portOptions?.length || 0); i++) {
          emptyRowData.push('-');
        }
        worksheet.addRow(emptyRowData);
        currentRow++;
      }

      // 添加汇总行
      const totalRowData = [
        '汇总',
        '',
        '',
        category.total?.realTon || '-',
        category.total?.monthVoyageNum || '-',
        category.total?.weekVoyageNum || '-'
      ];
      info.portOptions?.forEach(port => {
        const portTotalData = category.total?.monthPortDetail?.find(p => p.portId === port.value);
        totalRowData.push(portTotalData?.monthPortNum || '-');
      });
      worksheet.addRow(totalRowData);

      // 设置汇总行样式
      const summaryRow = worksheet.lastRow;
      summaryRow.eachCell(cell => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF00' }
        };
      });
      currentRow++;
    }
  }

  // --- 6. 设置所有单元格边框 ---
  worksheet.eachRow((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
  });

  // --- 7. 生成文件 ---
  // 在 Node.js 环境下
  // await workbook.xlsx.writeFile('宝镁事业部生产周报表.xlsx');
  // console.log('Excel 文件已生成！');

  // 在浏览器环境下
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = '宝镁事业部生产周报表.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// 辅助函数：将 YYYY-MM-DD 格式转换为 M月D日 格式
function formatDateForTitle(dateString) {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

// --- 使用示例 ---
// 假设你从组件中获取到了数据
// const info = this.info;
// const startDateStr = this.queryParams.date;
// const endDateStr = this.endDate; // 假设 this.endDate 已计算好
// generateExcel(info, startDateStr, endDateStr);